import React, { useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import CommonPopup from '../../../TheoryQuestionFrame/CommonPopup';
import eyeIcon from '../../../../static/icons/Eye.svg';
import deleteIcon from '../../../../static/img/deletered.svg';
import arrowDownIcon from '../../../../static/img/arrowdown2.svg';
import QuestionTooltip from '../../../../components2/QuestionTooltip';
import questionMark from '../../../../static/img/question-mark.svg';

export const SelectableFeedbackType = ({
  formData,
  updateFormData,
  allItems = [],
  onPreview,
  icon,
  label,
  tooltipText,
  selectedItem,
  getSelectedItem,
  onSelect,
  onDelete,
  goToRoute,
  popupItemType,
  hideTypeFilter = false,
  showLabel = true,
  showTooltip = true,
  filterSubject = null,
  compactTriggerLabel = null,
  placeholder = null,
  hideGoToLink = false,
}) => {
  const history = useHistory();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const handleSelect = (item) => {
    onSelect(item);
    setIsPopupOpen(false);
  };

  const handlePreview = () => {
    if (selectedItem && onPreview) {
      onPreview(selectedItem);
    }
  };

  const selected = selectedItem || getSelectedItem?.(formData, allItems);
  const hasValidSelection = selected && (selected.title || selected.name);
  const hasSelection = !!selected;

  return (
    <Container>
      {showLabel && (
        <InputHeader>
          <OptionIcon>
            <IconImage src={icon} alt={label} />
          </OptionIcon>
          <OptionContent>
            <OptionLabel>
              {label}
              {showTooltip && (
                <QuestionTooltip 
                  text={tooltipText || `Learn more about ${label}`} 
                  img={questionMark} 
                />
              )}
            </OptionLabel>
          </OptionContent>
        </InputHeader>
      )}
      
      <InputContent showLabel={showLabel && !compactTriggerLabel}>
        {compactTriggerLabel ? (
          <CompactTriggerButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsPopupOpen(true);
            }}
          >
            {compactTriggerLabel}
          </CompactTriggerButton>
        ) : (
          <>
            <SelectionRow>
              <SelectWrapper>
                <SelectButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPopupOpen(true);
                  }}
                >
                  <span>{hasValidSelection ? (selected.title || selected.name) : (placeholder || 'Select')}</span>
                  <SelectArrow src={arrowDownIcon} alt="dropdown" />
                </SelectButton>
              </SelectWrapper>
              {hasSelection && (
                <PreviewButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview();
                  }}
                >
                  <PreviewIcon src={eyeIcon} alt="preview" />
                </PreviewButton>
              )}
            </SelectionRow>
            {hasSelection && (
              <DeleteLink
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <DeleteIcon src={deleteIcon} alt="delete" />
                Delete
              </DeleteLink>
            )}
            {!hideGoToLink && (
              <GoToLink
                onClick={(e) => {
                  e.stopPropagation();
                  history.push(goToRoute);
                }}
              >
                Go to {label.toLowerCase()}
              </GoToLink>
            )}
          </>
        )}
      </InputContent>

      {isPopupOpen && (
        <CommonPopup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          allMarkingCriterias={allItems}
          updateMarkingCriteria={handleSelect}
          itemType={popupItemType}
          selectedItem={selected}
          hideTypeFilter={hideTypeFilter}
          filterSubject={filterSubject}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const InputHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const OptionIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const IconImage = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const OptionLabel = styled.span`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 16px;
  font-weight: 500;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding-left: ${props => props.showLabel ? '56px' : '0'};
`;

const SelectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

const SelectWrapper = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 10px 12px 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--text);
  min-height: 42px;
  overflow: hidden;
  
  & > span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  &:focus {
    outline: none;
    border-color: #7200E0;
    box-shadow: 0 0 0 3px rgba(114, 0, 224, 0.1);
  }
  
  &:hover {
    border-color: #7200E0;
  }
`;

const SelectArrow = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #7200E0;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  
  &:hover {
    background-color: #5a00b3;
  }
`;

const PreviewIcon = styled.img`
  filter: brightness(0) invert(1);
`;

const DeleteLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  font-weight: 500;
  color: #ef4444;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: fit-content;
  
  &:hover {
    color: #dc2626;
  }
`;

const DeleteIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const GoToLink = styled.button`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  font-weight: 500;
  color: #7200E0;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  width: fit-content;
  
  &:hover {
    color: #5a00b3;
  }
`;

const CompactTriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 20px;
  border-radius: 999px;
  border: 1px solid #7200e0;
  background: #fff;
  color: #7200e0;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  width: fit-content;

  &:hover {
    background: #f5f2ff;
  }
`;
